/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useEffect, useRef, useState } from "react"

import type { Theme } from "./theme-context"

import customerConfig from "@/customer/config.json"
import { getInjectedCssVariableNames } from "@/lib/runtime-branding"

import { ThemeProviderContext } from "./theme-context"

const DARK_QUERY = "(prefers-color-scheme: dark)"

/** Resolve "system" to the palette the OS is actually asking for (light where it can't be asked). */
function systemTheme(): "light" | "dark" {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches
    ? "dark"
    : "light"
}

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

// Re-export for backward compatibility
// eslint-disable-next-line react-refresh/only-export-components
export { useTheme } from "./use-theme"

export { ThemeProviderContext } from "./theme-context"
export type { Theme, ThemeProviderState } from "./theme-context"

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )
  // Inline custom properties this provider set, so they can be removed again
  // on the next theme change — see the effect below for why that matters.
  const appliedFallbackVars = useRef<Set<string>>(new Set())
  // Follow OS changes live, so "system" (a user's or the deployment's default,
  // #3298) repaints without a reload and consumers get one resolved value.
  const [osTheme, setOsTheme] = useState<"light" | "dark">(systemTheme)
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return
    const query = window.matchMedia(DARK_QUERY)
    const onChange = () => setOsTheme(query.matches ? "dark" : "light")
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])
  const resolvedTheme = theme === "system" ? osTheme : theme

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "dark")
    root.classList.add(resolvedTheme)
  }, [resolvedTheme])

  const value = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  useEffect(() => {
    // Server-injected branding (see lib/runtime-branding.ts) already painted
    // :root/.dark before this component mounted, so the build-time
    // config.json palette must not be applied on top of it — that would
    // override a branded tenant's colors with the generic defaults and
    // violate the injected > build-time precedence.
    //
    // Two things this has to get right, both found reviewing #2742:
    //
    // 1. Clear what a previous run set. These are INLINE properties on
    //    documentElement, and inline beats a stylesheet rule. A tenant with
    //    only a light palette who visits dark mode gets the build-time dark
    //    palette written inline; switching back to light would then skip
    //    this effect (light is branded) and leave those inline values
    //    overriding the injected :root rule — branding silently lost until
    //    a reload.
    //
    // 2. Skip per variable, not per theme. A branding record that sets three
    //    variables must not suppress the whole build-time palette: the
    //    variables it does not set should still come from config.json rather
    //    than falling through to the generic bundle CSS.
    const root = document.documentElement
    for (const name of appliedFallbackVars.current) {
      root.style.removeProperty(name)
    }
    appliedFallbackVars.current.clear()

    const injected = getInjectedCssVariableNames(resolvedTheme)
    const palette =
      resolvedTheme === "dark"
        ? customerConfig.config.colors.dark
        : customerConfig.config.colors.light

    for (const [key, value] of Object.entries(palette)) {
      if (injected.has(key)) {
        continue
      }
      root.style.setProperty(key, value)
      appliedFallbackVars.current.add(key)
    }
  }, [resolvedTheme])

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}
